import { BaseEntity } from './base.entity';
import { ProjectUserRole } from './project-user';

export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired';

export class ProjectInvitation extends BaseEntity {
  id: string;
  project_id: string;
  email: string;
  role: ProjectUserRole;
  invited_by_id: string;
  token: string;
  status: InvitationStatus;
  expires_at: Date;
  created_at: Date;
  updated_at: Date;
  accepted_at: Date | null;

  constructor(data: {
    id: string;
    project_id: string;
    email: string;
    role: ProjectUserRole;
    invited_by_id: string;
    token: string;
    status: InvitationStatus;
    expires_at: Date;
    created_at: Date;
    updated_at: Date;
    accepted_at: Date | null;
  }) {
    super();
    this.id = data.id;
    this.project_id = data.project_id;
    this.email = data.email;
    this.role = data.role;
    this.invited_by_id = data.invited_by_id;
    this.token = data.token;
    this.status = data.status;
    this.expires_at = data.expires_at;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.accepted_at = data.accepted_at;
    this.setOriginalData(this.toData());
  }

  /**
   * Accept the invitation
   */
  accept(): void {
    this.status = 'accepted';
    this.accepted_at = new Date();
    this.markAsDirty();
  }

  /**
   * Decline the invitation
   */
  decline(): void {
    this.status = 'declined';
    this.markAsDirty();
  }

  /**
   * Expire the invitation
   */
  expire(): void {
    this.status = 'expired';
    this.markAsDirty();
  }

  /**
   * Check if invitation is expired
   */
  isExpired(): boolean {
    return this.status === 'expired' || new Date() > this.expires_at;
  }

  /**
   * Check if invitation can be accepted
   */
  canBeAccepted(): boolean {
    return this.status === 'pending' && !this.isExpired();
  }

  /**
   * Convert to plain data object
   */
  toData(): any {
    return {
      id: this.id,
      project_id: this.project_id,
      email: this.email,
      role: this.role,
      invited_by_id: this.invited_by_id,
      token: this.token,
      status: this.status,
      expires_at: this.expires_at instanceof Date ? this.expires_at.toISOString() : this.expires_at,
      created_at: this.created_at instanceof Date ? this.created_at.toISOString() : this.created_at,
      updated_at: this.updated_at instanceof Date ? this.updated_at.toISOString() : this.updated_at,
      accepted_at: this.accepted_at instanceof Date ? this.accepted_at.toISOString() : this.accepted_at
    };
  }

  /**
   * Create ProjectInvitation instance from database row
   */
  static fromData(data: any): ProjectInvitation {
    return new ProjectInvitation({
      id: data.id,
      project_id: data.project_id,
      email: data.email,
      role: data.role,
      invited_by_id: data.invited_by_id,
      token: data.token,
      status: data.status,
      expires_at: new Date(data.expires_at),
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at),
      accepted_at: data.accepted_at ? new Date(data.accepted_at) : null
    });
  }
}

export interface ProjectInvitationInsert {
  project_id: string;
  email: string;
  role: ProjectUserRole;
  invited_by_id: string;
}

export interface ProjectInvitationUpdate {
  status?: InvitationStatus;
  accepted_at?: Date | null;
}